import {
    Component,
    ElementRef,
    HostListener,
    Injector,
    Input,
    OnChanges,
    OnInit,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { createCustomElement } from '@angular/elements';
import { DagreLayout, Edge as layoutEdge, Model, Node as layoutNode } from '@antv/layout';
import { Edge, Graph, Markup, Node } from '@antv/x6';
import { Scroller } from '@antv/x6-plugin-scroller';
import { RadioResourceList, ResourceConnection } from '@app/interfaces/rm/resource-connection';
import { ResourceInstances, ResourceInstancesLocation } from '@app/interfaces/rm/resource-instances';
import { ResourceType } from '@app/interfaces/rm/resources';
import { PortComponent } from '@app/modules/product/rm/dashboard/components/dashboard/components/port/port.component';
import { ChamberHttp } from '@services/rm/chamber.service';
import { ResourcesService } from '@services/rm/resources.service';


export interface ChartPort {
    id: string;
    name: string;
    group: string;
}
@Component({
    selector: 'app-resource-connection-chart',
    templateUrl: './resource-connection-chart.component.html',
    styleUrls: ['./resource-connection-chart.component.less'],
})
export class ResourceConnectionChartComponent implements OnInit, OnChanges {
    @Input() chartData: ResourceConnection[] = [];
    @Input() allResourceType: ResourceType[] = [];
    @ViewChild('chartContainer') chartContainer!: ElementRef;
    @ViewChild('container') container!: ElementRef;
    @ViewChild('edgeTooltip') edgeTooltip!: ElementRef;
    @ViewChild('nodeTooltip') nodeTooltip!: ElementRef;
    @ViewChild('minimapContainer') minimapContainer!: ElementRef;

    graph!: Graph;

    graphBasicConfig = {
        // panning: true,
        selecting: true,
        autoResize: true,
        mousewheel: {
            enabled: true,
        },
        async: true,
        scroller: {
            enabled: true,
            pannable: true,
        },
        scaling: {
            min: 0.5,
        },
    };

    nodes: Node.Metadata[] = [];
    edges: Edge.Metadata[] = [];

    layoutConfig = {
        minNodeWidth: 80,
        nodeHeight: 40,
        highlightColor: '#61AFFE',
        highlightFillColor: '#EBF3FB',
    };

    nodeAttrs = {
        body: {
            stroke: this.layoutConfig.highlightColor,
            strokeWidth: 1,
            fill: this.layoutConfig.highlightFillColor,
        },
    };
    edgeAttrs = {
        line: {
            stroke: this.layoutConfig.highlightColor,
            strokeWidth: 1,
            targetMarker: {
                name: 'classic',
                size: 7,
            },
        },
    };
    portConfig = {
        attrs: {
            fo: {
                width: 12,
                height: 12,
                x: -6,
                y: -6,
                stroke: this.layoutConfig.highlightColor,
            },
        },
    };
    currentEdgeInfo: ResourceConnection[] = [];
    currentNodeInfo: ResourceInstances | undefined;
    timer: number | null | undefined;
    groups = {
        source: {
            ...this.portConfig,
            position: 'right',
        },
        target: {
            ...this.portConfig,
            position: 'left',
        },
    };
    sourceArr: string[] = [];
    canEditPortNameResource = ['CHAMBER', 'RADIO', ...this.resourceService.getOtherCanEditPortResource()];

    constructor(private injector: Injector,
                private chamberHttp: ChamberHttp,
                private resourceService: ResourcesService) {
    }

    @HostListener('window:resize', ['$event.target'])
    onResize(): void {
        if (this.timer) {
            clearTimeout(this.timer);
            this.timer = null;
        }
        this.timer = window.setTimeout(() => {
            if (this.graph) {
                const width = this.chartContainer.nativeElement.offsetWidth;
                const height = this.chartContainer.nativeElement.offsetHeight;
                this.graph.resize(width, height);
                this.graph.centerContent();
            }
        }, 800);
    }

    ngOnInit(): void {
        this.onResize();
    }

    formatResourceName(resourceName: string): string {
        const data = this.allResourceType?.find((item) => item.key === resourceName || item.value === resourceName);
        return data ? data.label : '';
    }

    getLocation(location: ResourceInstancesLocation): string {
        return location?.parentNames ? (location?.parentNames.replace(/,/g, '/') + '/' + location?.name) : (location?.name ? location?.name : '');
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.container) {
            return;
        }
        if (!this.graph) {
            this.initGraph();
            this.initEvent();
        }
        this.refreshChart();
    }

    initGraph(): void {
        const width = this.chartContainer.nativeElement.offsetWidth;
        const height = this.chartContainer.nativeElement.offsetHeight;
        const elementName = 'custom-port';
        const hasDefine = customElements.get(elementName);
        if (!hasDefine) {
            const customPort = createCustomElement(PortComponent, { injector: this.injector });
            customElements.define(elementName, customPort);
        }
        const graphConfig = {
            width,
            height,
            ...this.graphBasicConfig,
            minimap: {
                enabled: true,
                container: this.minimapContainer.nativeElement,
            },
            // scroller: {
            //     enabled: true,
            //     pannable: true,
            //     pageVisible: false,
            //     pageBreak: false,
            //     padding: 10,
            //     autoResize: true
            // },
            container: this.container.nativeElement,
            tools: [
                {
                    name: 'tooltip',
                    args: {
                        tooltip: 'Tooltip Content',
                    },
                },
            ],
            onPortRendered: (args: any): void => {
                const selectors = args.contentSelectors;
                const container = selectors && selectors.foContent;
                if (container) {
                    const p = document.createElement(elementName);
                    const portInfo = args?.port;
                    p.setAttribute('id', portInfo?.id);
                    p.setAttribute('name', portInfo?.name);
                    p.setAttribute('stroke', portInfo.attrs.fo.stroke);
                    container.appendChild(p);
                }
            },
        };
        this.graph = new Graph(graphConfig);
        this.graph.use(
            new Scroller({
                enabled: true,
            }),
        )
    }

    initEvent(): void {
        this.graph.lockScroller();
        this.graph.on('edge:mouseenter', ({ e, cell }) => {
            const source = cell.getSource() as Edge.TerminalCellData;
            const target = cell.getTarget() as Edge.TerminalCellData;
            this.currentEdgeInfo = this.chartData.filter(item => item.sourceResourceInstance.id.toString() === source.cell && item.targetResourceInstance.id.toString() === target.cell
            && item.sourcePort === source.port && item.targetPort === target.port);
            this.edgeTooltip.nativeElement.style.left = e.clientX + 'px';
            this.edgeTooltip.nativeElement.style.top = e.clientY + 'px';
            this.edgeTooltip.nativeElement.style.display = 'block';
            this.edgeTooltip.nativeElement.style.zIndex = 1999;
        });

        this.graph.on('edge:mouseleave', ({ e, edge, view }) => {
            this.edgeTooltip.nativeElement.style.display = 'none';
        });


        this.graph.on('node:mouseenter', ({ e, cell }) => {
            const cellId = cell.id;
            const currentItem = this.chartData.find(item => item.sourceResourceInstance.id.toString() === cellId.toString() || item.targetResourceInstance.id.toString() === cellId.toString());
            this.currentNodeInfo = currentItem?.sourceResourceInstance.id.toString() === cellId ? currentItem?.sourceResourceInstance : currentItem?.targetResourceInstance;
            this.nodeTooltip.nativeElement.style.left = e.clientX + 'px';
            this.nodeTooltip.nativeElement.style.top = e.clientY + 'px';
            this.nodeTooltip.nativeElement.style.display = 'block';
            this.nodeTooltip.nativeElement.style.zIndex = 1999;
        });

        this.graph.on('node:mouseleave', ({ e, node, view }) => {
            this.nodeTooltip.nativeElement.style.display = 'none';
        });
    }

    // portList only radio
    getPorts(portNum: number, resourceName: string): ChartPort[] {
        const arr = [];
        for (let i = 1; i <= portNum; i++) {
            const label = resourceName + '-' + i;
            arr.push({
                id: label,
                name: label,
                group: this.sourceArr.includes(label) ? 'source' : 'target'
            });
        }
        return arr;
    }

    // target resource has left port as INPUT
    getMatrixPorts(inputNum: number, outputNum: number, name: string): ChartPort[] {
        const arr: ChartPort[] = [];
        for (let i = 1; i <= inputNum; i++) {
            const label = 'INPUT' + '_' + i;
            arr.push({
                id: label,
                name: label,
                group: 'target'
            });
        }
        for (let i = 1; i <= outputNum; i++) {
            const label = 'OUTPUT' + '_' + i;
            arr.push({
                id: label,
                name: label,
                group: 'source'
            });
        }
        return arr;
    }

    getSourceAndTargetArr(): void {
        this.sourceArr = [];
        if (this.chartData?.length <= 0) {
            return ;
        }
        this.chartData?.forEach(item => {
            this.sourceArr = [...this.sourceArr, item.sourcePort];
        });
    }

    getMatrixMaxPortNum(inputNum: number|null, outputNum: number|null): number|null {
        return (!inputNum || !outputNum) ? null : (outputNum - inputNum > 0 ? outputNum : inputNum);
    }

    // source---right port---output, target---left port---input
    async refreshChart(): Promise<void> {
        this.nodes = [];
        this.edges = [];
        this.getSourceAndTargetArr();
        for (const item of this.chartData) {
            const { sourceMatrixOutputPort, sourceMatrixInputPort, sourceRadioPortVOList } = item;
            const matrixPort = this.getMatrixMaxPortNum(sourceMatrixInputPort, sourceMatrixOutputPort);
            const sourceId = item.sourceResourceInstance.id.toString();
            console.log('item', item);
            if (!this.nodes.some(node => node.id === sourceId)) {
                const { name, port, id, hardwareType } = item.sourceResourceInstance;
                const sourcePort = matrixPort || sourceRadioPortVOList?.length || port || 0;
                const otherResourcePortList = await this.getOtherResourcePort(Number(sourceId), hardwareType, item.sourceRadioPortVOList);
                this.nodes.push({
                    id: sourceId,
                    width: this.getNodeWidth(name),
                    height: this.getNodeHeight(sourcePort),
                    label: item.sourceResourceInstance.name,
                    attrs: this.nodeAttrs,
                    portMarkup: [Markup.getForeignObjectMarkup()],
                    ports: {
                        groups: this.groups,
                        items: sourceMatrixOutputPort && sourceMatrixInputPort ? this.getMatrixPorts(sourceMatrixInputPort, sourceMatrixOutputPort, name) :
                            (this.canEditPortNameResource.includes(hardwareType) ? otherResourcePortList : this.getPorts(sourcePort, name))
                    }
                });
            }
            const targetId = item.targetResourceInstance.id.toString();
            if (!this.nodes.some(node => node.id === targetId)) {
                const {name, port, hardwareType} = item.targetResourceInstance;
                const { targetMatrixOutputPort, targetMatrixInputPort, targetRadioPortVOList } = item;
                const matrixTargetPort = this.getMatrixMaxPortNum(targetMatrixInputPort, targetMatrixOutputPort);
                const targetPort = matrixTargetPort || targetRadioPortVOList?.length || port || 0;
                const otherResourcePortList = await this.getOtherResourcePort(Number(targetId), hardwareType, item.targetRadioPortVOList);
                this.nodes.push({
                    id: targetId,
                    width: this.getNodeWidth(item.targetResourceInstance.name),
                    height: this.getNodeHeight(targetPort),
                    label: item.targetResourceInstance.name,
                    attrs: this.nodeAttrs,
                    portMarkup: [Markup.getForeignObjectMarkup()],
                    ports: {
                        groups: this.groups,
                        items: targetMatrixOutputPort && targetMatrixInputPort ? this.getMatrixPorts(targetMatrixInputPort, targetMatrixOutputPort, name) :
                            (this.canEditPortNameResource.includes(hardwareType) ? otherResourcePortList : this.getPorts(targetPort || 0, name))
                    }
                });
            }

            console.log('nodes', this.nodes);

            this.edges.push({
                source: {
                    cell: sourceId,
                    port: item.sourcePort
                },
                target: {
                    cell: targetId,
                    port: item.targetPort
                },
                attrs: this.edgeAttrs,
                router: {
                    name: 'er',
                    args: {
                        offset: 24,
                        direction: 'L'
                    },
                },
            });
        }
        console.log('edges', this.edges);
        const dagreLayout = new DagreLayout({
            type: 'dagre',
            rankdir: 'LR',
            align: undefined,
            nodeSize: 100,
            nodesepFunc: (res) => {
                return res.height / 2;
            },
            // ranksepFunc: (res) => {
            //     return res.height / 3;
            // }
        });
        const modelData: Model = {
            nodes: this.nodes as layoutNode[],
            edges: this.edges as layoutEdge[],
        };
        const model: Model = dagreLayout.layout(modelData);
        this.graph.fromJSON(model);
    }

    getNodeWidth(label: string): number {
        const labelWidth = (label?.length || 1) * 10 + 10;
        const {
            minNodeWidth,
        } = this.layoutConfig;
        return minNodeWidth > labelWidth ? minNodeWidth : labelWidth;
    }

    getNodeHeight(portNum: number): number {
        const {
            nodeHeight,
        } = this.layoutConfig;
        const portHeight =  16 * portNum;
        console.log('getNodeHeight', portNum, portHeight, nodeHeight, portHeight > nodeHeight ? portHeight : nodeHeight);
        return portHeight > nodeHeight ? portHeight : nodeHeight;
    }

    async getOtherResourcePort(resourceInstanceId: number, resourceType: string, radioPortList?: RadioResourceList[]): Promise<ChartPort[]> {
        let portList: ChartPort[] = [];
        if (resourceType === 'CHAMBER') {
            const res = await this.chamberHttp.getChamberNameByInstanceId({
                resourceInstanceId
            });
            portList = res?.map(item => {
                return {
                    id: item,
                    name: item,
                    group: 'target'
                };
            });
        } else if (resourceType === 'RADIO') {
            portList = radioPortList?.map(item => {
                const portName = item.portName;
                return {
                    id: portName,
                    name: portName,
                    group: this.sourceArr.includes(item.portName) ? 'source' : 'target'
                };
            }) || [];
        } else {
            const res = await this.resourceService.getPortList({
                resourceInstanceId,
                page: 0,
                size: 999999
            });
            portList = res?.content?.map(item => {
                return {
                    id: item.portName,
                    name: item.portName,
                    group: this.sourceArr.includes(item.portName) ? 'source' : 'target'
                };
            });
        }
        return portList;
    }
}
